import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Theme colors from design system
export const themeColors = {
  body: '#063142', // Body color - dark navy
  white: '#FFFFFF',
  light1: '#F8F8F8', // Light 1
  light2: '#E7EFF5', // Light 2
  turq: '#00B6BA', // Theme Primary Turquoise
  turqLight: '#CCF2F3', // Lighter turquoise for switches
  blue: '#00ACE7', // Theme Primary Blue
  navy: '#063142', // Theme Primary Navy
  midGrey: '#9CADB4', // Mid 2 color
};

// Styled components for segmented controls
export const SegmentedControlContainer = styled(Box)(() => ({
  display: 'flex',
  borderRadius: '4px',
  overflow: 'hidden',
  border: `1px solid ${themeColors.turq}`,
  height: '30px',
  width: '100%',
}));

export const SegmentButton = styled(Box)<{ active: boolean }>(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 16px',
  height: '100%',
  flex: 1,
  cursor: 'pointer',
  backgroundColor: active ? themeColors.turq : 'transparent',
  color: active ? themeColors.white : themeColors.turq,
  fontFamily: '"Lato-Bold", sans-serif',
  fontSize: '14px',
  fontWeight: 700,
  transition: 'background-color 0.2s, color 0.2s',
  '&:hover': {
    backgroundColor: active ? themeColors.turq : 'rgba(0, 182, 186, 0.1)',
  },
  borderRight: `1px solid ${themeColors.turq}`,
  '&:last-child': {
    borderRight: 'none',
  },
}));

// Styled components for module toggles
export const ModuleToggleContainer = styled(Box)(() => ({
  backgroundColor: themeColors.light1,
  borderRadius: '8px',
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  marginBottom: '8px',
}));

export const ModuleToggleLabel = styled(Typography)(() => ({
  color: themeColors.body,
  fontFamily: '"Lato-Bold", sans-serif',
  fontSize: '12px',
  fontWeight: 700,
  textTransform: 'uppercase',
}));

export const SelectAllButton = styled(Typography)(() => ({
  color: themeColors.blue,
  fontFamily: '"Lato-Bold", sans-serif',
  fontSize: '12px',
  fontWeight: 700,
  cursor: 'pointer',
}));

export const ModuleSection = styled(Box)(() => ({
  borderRight: `1px solid ${themeColors.light2}`,
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '240px',
  flexShrink: 0,
}));

export const ModuleSectionHeader = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  marginBottom: '8px',
}));

export const ModuleSectionTitle = styled(Typography)(() => ({
  color: themeColors.body,
  fontFamily: '"Lato-Bold", sans-serif',
  fontSize: '12px',
  fontWeight: 700,
}));

export const CheckboxList = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
}));

export const FormContainer = styled(Box)(() => ({
  display: 'flex', 
  flexDirection: 'column', 
  gap: 24, 
  width: '100%', 
  backgroundColor: themeColors.white, 
  padding: 24, 
  borderRadius: 8,
}));

export const FormSection = styled(Box)(() => ({
  display: 'flex', 
  gap: 32,
}));

export const LeftColumn = styled(Box)(() => ({
  display: 'flex', 
  flexDirection: 'column', 
  gap: 24, 
  flex: 1,
}));

export const RightColumn = styled(Box)(() => ({
  display: 'flex', 
  flexDirection: 'column', 
  gap: 4, 
  width: '315px',
}));

export const FieldLabel = styled(Typography)(() => ({
  fontSize: '12px', 
  fontWeight: 700, 
  color: themeColors.body,
  marginBottom: 8,
}));

export const HelperText = styled(Typography)(() => ({
  display: 'block', 
  marginTop: 4, 
  color: themeColors.midGrey,
  fontSize: '11px',
}));

export const PermissionsContainer = styled(Box)(() => ({
  display: 'flex', 
  overflowX: 'auto',
}));

// Custom Switch styled components
export const SwitchContainer = styled(Box)(() => ({
  position: 'relative',
  width: 51,
  height: 26,
  padding: 0,
  borderRadius: 13,
  cursor: 'pointer',
  transition: 'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1)'
}));

export const SwitchThumb = styled(Box)<{ checked: boolean }>(({ checked }) => ({
  position: 'absolute',
  right: checked ? 3 : 'auto',
  left: checked ? 'auto' : 3,
  top: 3,
  width: 20,
  height: 20,
  backgroundColor: themeColors.white,
  borderRadius: '50%',
  transition: 'left 300ms cubic-bezier(0.4, 0, 0.2, 1), right 300ms cubic-bezier(0.4, 0, 0.2, 1)'
}));

// Custom Checkbox styled components
export const CheckboxContainer = styled(Box)(() => ({
  display: 'flex', 
  alignItems: 'center', 
  gap: 16, 
  marginBottom: 12,
  cursor: 'pointer',
  '&:hover': {
    '& .checkbox-box': {
      borderColor: themeColors.blue,
    }
  }
}));

export const CheckboxBox = styled(Box)<{ checked: boolean }>(({ checked }) => ({
  width: 16, 
  height: 16, 
  borderRadius: 4, 
  backgroundColor: checked ? themeColors.blue : 'transparent',
  border: `1px solid ${checked ? themeColors.blue : '#ccc'}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s',
}));

export const CheckboxLabel = styled(Typography)(() => ({
  fontSize: 14, 
  color: themeColors.body,
  fontFamily: '"Lato-Regular", sans-serif',
})); 